// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAggregationData } from '@shared/advisory/aggregation/providers';
import {
  capitalizeString,
  formatToMoney,
  formatToPercent,
  groupBy,
  useTabsController,
  type KpiBoxesProps,
} from '@shared/ui-kit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllocationAssetAnalysis } from './use-allocation-asset-analysis';
import { usePeriodPicker } from '@shared/advisory/aggregation/hooks';
import { usePortfoliosData } from '@shared/ui-kit/providers/portfolios-provider/portfolios-provider';

export const useAllocation = () => {
  const { t } = useTranslation();

  const {
    status: holdingsStatus,
    onChangeBenchmark,
    benchmark,
    currency,
    institutionId,
    onRefresh,
    accountsPositions,
    hasHoldings,
  } = useAggregationData();

  const periodPicker = usePeriodPicker();
  const { savedBenchmarks } = usePortfoliosData();

  const assetAnalysisPayload = useMemo(
    () => ({
      hist: periodPicker.period,
      holdings: accountsPositions.holdings,
      benchmark: [benchmark],
    }),
    [accountsPositions.holdings, benchmark, periodPicker.period],
  );

  const { assetAnalysis, analysisQuery } =
    useAllocationAssetAnalysis(assetAnalysisPayload);

  const tabsController = useTabsController('overview');

  const tabsOptions = useMemo(() => {
    return [
      {
        value: 'overview',
        label: t('common.overview', { ns: 'common' }) as string,
      },
      {
        value: 'fund',
        label: t('common.fund', { ns: 'common' }) as string,
        disabled: assetAnalysis.fund === null,
      },
      {
        value: 'etf',
        label: t('common.etf', { ns: 'common' }) as string,
        disabled: assetAnalysis.etf === null,
      },
      {
        value: 'stock',
        label: t('common.stock', { ns: 'common' }) as string,
        disabled: assetAnalysis.stock === null,
      },
    ];
  }, [assetAnalysis.fund, assetAnalysis.etf, assetAnalysis.stock, t]);

  const totalInvestment = useMemo(
    () => accountsPositions.investment,
    [accountsPositions.investment],
  );

  const equityAllocation = useMemo(
    () =>
      accountsPositions.assetClassHoldings.find(
        holding => holding.asset_class === 'Equity',
      )?.weight ?? 0,
    [accountsPositions.assetClassHoldings],
  );

  const fixedIncomeAllocation = useMemo(
    () =>
      accountsPositions.assetClassHoldings.find(
        holding => holding.asset_class === 'Fixed Income',
      )?.weight ?? 0,
    [accountsPositions.assetClassHoldings],
  );

  const kpis = useMemo<KpiBoxesProps[]>(
    () => [
      {
        text: t('common.investment', { ns: 'common' }),
        value: formatToMoney(totalInvestment, currency, 0),
        dataTestid: 'kpi-investment',
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        text: t('glossary.equity-allocation', { ns: 'glossary' }),
        value: formatToPercent(equityAllocation, 1),
        dataTestid: 'kpi-equity-allocation',
        icon: 'ChartPie',
        color: 'main',
      },
      {
        text: t('glossary.fixed-income-allocation', { ns: 'glossary' }),
        value: formatToPercent(fixedIncomeAllocation, 1),
        dataTestid: 'kpi-fixed-income-allocation',
        icon: 'Layers',
        color: 'secondary',
      },
    ],
    [currency, equityAllocation, fixedIncomeAllocation, t, totalInvestment],
  );

  const assetClassAllocation = useMemo(() => {
    const holdings = accountsPositions.assetClassHoldings.slice(1);

    return {
      labels: holdings.map(holding => holding.asset_class ?? 'Other'),
      values: holdings.map(holding => holding.weight),
    };
  }, [accountsPositions.assetClassHoldings]);

  const banksAllocation = useMemo(() => {
    const banks = groupBy(accountsPositions.holdings, 'institution_name');

    return {
      labels: Array.from(banks.keys()),
      values: Array.from(banks.values()).map(holding =>
        holding.reduce((acc, holding) => acc + holding.weight, 0),
      ),
    };
  }, [accountsPositions.holdings]);

  const currencyAllocation = useMemo(() => {
    const currencies = groupBy(accountsPositions.holdings, 'currency');

    return {
      labels: Array.from(currencies.keys()),
      values: Array.from(currencies.values()).map(holding =>
        holding.reduce((acc, holding) => acc + holding.weight, 0),
      ),
    };
  }, [accountsPositions.holdings]);

  const vehiclesAllocation = useMemo(() => {
    const holdings = accountsPositions.assetTypeHoldings.slice(1);

    return {
      labels: holdings.map(holding => capitalizeString(holding.asset_type)),
      values: holdings.map(holding => holding.weight),
    };
  }, [accountsPositions.assetTypeHoldings]);

  const status = useMemo<typeof holdingsStatus>(() => {
    const isLoading = [holdingsStatus === 'pending', analysisQuery.isLoading].some(
      Boolean,
    );

    const isError = [holdingsStatus === 'rejected', analysisQuery.isError].some(
      Boolean,
    );

    const isFetching = [
      holdingsStatus === 'fetching',
      analysisQuery.isFetching,
    ].some(Boolean);

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [
    analysisQuery.isError,
    analysisQuery.isFetching,
    analysisQuery.isLoading,
    holdingsStatus,
  ]);

  return {
    t,
    kpis,
    status,
    periodPicker,
    onChangeBenchmark,
    benchmark,
    hasHoldings,
    banksAllocation,
    currencyAllocation,
    vehiclesAllocation,
    assetClassAllocation,
    tabsController,
    assetAnalysis,
    tabsOptions,
    currency,
    institutionId,
    onRefresh,
    savedBenchmarks,
  };
};
